import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { useCustomerAuth } from "./CustomerAuthContext";
import {
  addWishlistItem,
  getWishlist,
  moveWishlistItemToCart,
  removeWishlistItem,
} from "../services/wishlistService";

const WishlistContext = createContext(null);
export const useWishlist = () => useContext(WishlistContext);

const STORAGE_KEY = "cynos_wishlist_v1";
const clean = (value) => String(value || "").trim();
const itemKey = (productId, color = "", size = "") =>
  `${clean(productId)}::${clean(color).toLowerCase()}::${clean(size).toLowerCase()}`;

const readGuestWishlist = () => {
  try {
    const parsed = JSON.parse(localStorage.getItem(STORAGE_KEY) || "[]");
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
};

const writeGuestWishlist = (items) => {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
};

const normalizeApiItems = (data) => data?.products || [];
const itemFromProduct = (product, color = "", size = "") => ({
  productId: product.id,
  selectedColor: clean(color),
  selectedSize: clean(size),
  createdAt: new Date().toISOString(),
  product,
});

export const WishlistProvider = ({ children }) => {
  const { isAuthenticated, loading: authLoading, token } = useCustomerAuth();
  const [items, setItems] = useState(() => readGuestWishlist());
  const [loading, setLoading] = useState(false);
  const [pendingKeys, setPendingKeys] = useState({});

  const persistGuestItems = useCallback((nextItems) => {
    setItems(nextItems);
    writeGuestWishlist(nextItems);
  }, []);

  const refresh = useCallback(async () => {
    if (!isAuthenticated) {
      const guestItems = readGuestWishlist();
      setItems(guestItems);
      return guestItems;
    }
    setLoading(true);
    try {
      const data = await getWishlist();
      const nextItems = normalizeApiItems(data);
      setItems(nextItems);
      return nextItems;
    } catch (err) {
      console.warn("Wishlist refresh failed", err);
      return items;
    } finally {
      setLoading(false);
    }
  }, [isAuthenticated, items]);

  useEffect(() => {
    if (authLoading) return undefined;
    let cancelled = false;

    const syncWishlist = async () => {
      if (!isAuthenticated) {
        setItems(readGuestWishlist());
        return;
      }

      setLoading(true);
      try {
        const guestItems = readGuestWishlist();
        if (guestItems.length) {
          await Promise.allSettled(
            guestItems.map((item) =>
              addWishlistItem({
                productId: item.productId,
                color: item.selectedColor || "",
                size: item.selectedSize || "",
              })
            )
          );
          writeGuestWishlist([]);
        }
        const data = await getWishlist();
        if (!cancelled) setItems(normalizeApiItems(data));
      } catch (err) {
        console.warn("Wishlist synchronization failed", err);
        if (!cancelled) setItems(readGuestWishlist());
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    syncWishlist();
    return () => {
      cancelled = true;
    };
  }, [authLoading, isAuthenticated, token]);

  const isInWishlist = useCallback(
    (productId, color = "", size = "") =>
      items.some((item) => itemKey(item.productId, item.selectedColor, item.selectedSize) === itemKey(productId, color, size)),
    [items]
  );

  const setPending = useCallback((key, value) => {
    setPendingKeys((previous) => ({ ...previous, [key]: value }));
  }, []);

  const addItem = useCallback(
    async (product, color = "", size = "", options = {}) => {
      const key = itemKey(product.id, color, size);
      if (pendingKeys[key]) return false;
      if (isInWishlist(product.id, color, size)) return true;

      const previousItems = items;
      const nextItem = itemFromProduct(product, color, size);
      setPending(key, true);
      try {
        if (isAuthenticated) {
          setItems([...items, nextItem]);
          const data = await addWishlistItem({ productId: product.id, color, size });
          setItems(normalizeApiItems(data));
        } else {
          persistGuestItems([...items, nextItem]);
        }
        if (options.notify !== false) toast.success(options.successMessage || "Added to wishlist");
        return true;
      } catch (err) {
        if (isAuthenticated) setItems(previousItems);
        else persistGuestItems(previousItems);
        toast.error(options.errorMessage || "Could not save to wishlist. Please try again.");
        return false;
      } finally {
        setPending(key, false);
      }
    },
    [isAuthenticated, isInWishlist, items, pendingKeys, persistGuestItems, setPending]
  );

  const removeItem = useCallback(
    async ({ productId, selectedColor = "", selectedSize = "" }) => {
      const key = itemKey(productId, selectedColor, selectedSize);
      if (pendingKeys[key]) return false;

      const previousItems = items;
      const nextItems = items.filter((item) => itemKey(item.productId, item.selectedColor, item.selectedSize) !== key);
      setPending(key, true);
      try {
        if (isAuthenticated) {
          setItems(nextItems);
          const data = await removeWishlistItem({ productId, color: selectedColor, size: selectedSize });
          setItems(normalizeApiItems(data));
        } else {
          persistGuestItems(nextItems);
        }
        toast.success("Removed from wishlist");
        return true;
      } catch (err) {
        if (isAuthenticated) setItems(previousItems);
        else persistGuestItems(previousItems);
        toast.error("Could not update wishlist. Please try again.");
        return false;
      } finally {
        setPending(key, false);
      }
    },
    [isAuthenticated, items, pendingKeys, persistGuestItems, setPending]
  );

  const toggleItem = useCallback(
    async (product, color = "", size = "") => {
      if (isInWishlist(product.id, color, size)) {
        return removeItem({ productId: product.id, selectedColor: color, selectedSize: size });
      }
      return addItem(product, color, size);
    },
    [addItem, isInWishlist, removeItem]
  );

  const updateItemVariant = useCallback(
    async (productId, size, color = "", nextSize, nextColor = "") => {
      const previousKey = itemKey(productId, color, size);
      const nextKey = itemKey(productId, nextColor, nextSize);
      if (previousKey === nextKey) return true;
      if (pendingKeys[previousKey] || pendingKeys[nextKey]) return false;

      const previousItem = items.find((item) => itemKey(item.productId, item.selectedColor, item.selectedSize) === previousKey);
      if (!previousItem) return false;

      const nextItemExists = items.some((item) => itemKey(item.productId, item.selectedColor, item.selectedSize) === nextKey);
      const updatedItem = { ...previousItem, selectedColor: nextColor, selectedSize: nextSize };
      const nextItems = items.filter((item) => itemKey(item.productId, item.selectedColor, item.selectedSize) !== previousKey);
      if (!nextItemExists) nextItems.push(updatedItem);

      const previousItems = items;
      setPending(previousKey, true);
      if (nextKey !== previousKey) setPending(nextKey, true);
      try {
        if (isAuthenticated) {
          if (!nextItemExists) {
            await addWishlistItem({ productId, color: nextColor, size: nextSize });
          }
          await removeWishlistItem({ productId, color, size });
          const data = await getWishlist();
          setItems(normalizeApiItems(data));
        } else {
          persistGuestItems(nextItems);
        }
        return true;
      } catch (err) {
        setItems(previousItems);
        toast.error("Unable to update wishlist selection. Please try again.");
        return false;
      } finally {
        setPending(previousKey, false);
        if (nextKey !== previousKey) setPending(nextKey, false);
      }
    },
    [isAuthenticated, items, pendingKeys, persistGuestItems, setPending]
  );

  const moveToCart = useCallback(
    async (item) => {
      const key = itemKey(item.productId, item.selectedColor, item.selectedSize);
      if (pendingKeys[key]) return null;
      const previousItems = items;
      setPending(key, true);
      try {
        if (isAuthenticated) {
          setItems(items.filter((entry) => itemKey(entry.productId, entry.selectedColor, entry.selectedSize) !== key));
          const moved = await moveWishlistItemToCart(item);
          return moved;
        }
        persistGuestItems(items.filter((entry) => itemKey(entry.productId, entry.selectedColor, entry.selectedSize) !== key));
        return item;
      } catch (err) {
        setItems(previousItems);
        toast.error("Could not move wishlist item right now");
        return null;
      } finally {
        setPending(key, false);
      }
    },
    [isAuthenticated, items, pendingKeys, persistGuestItems, setPending]
  );

  const totalCount = items.length;
  const value = useMemo(
    () => ({
      items,
      loading,
      pendingKeys,
      totalCount,
      addItem,
      removeItem,
      toggleItem,
      moveToCart,
      updateItemVariant,
      isInWishlist,
      refresh,
    }),
    [addItem, isInWishlist, items, loading, moveToCart, pendingKeys, refresh, removeItem, toggleItem, totalCount, updateItemVariant]
  );

  return <WishlistContext.Provider value={value}>{children}</WishlistContext.Provider>;
};
